/**
 * video.ts — Categoria "Vídeo" (10 modelos premium)
 *
 * Apresentações em vídeo com capa do acervo compartilhado, texto de apoio,
 * números de prova e chamadas para ação.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Player de verdade (link normal do YouTube/Vimeo),
 *            capa do acervo, formato do quadro e faixa com margem lateral
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para o vídeo com capa do acervo. */
function video(capa: string, props: Record<string, unknown> = {}) {
  return b('ui.video', {
    // Endereço de exemplo: a pessoa troca pelo link do próprio vídeo. Basta
    // colar o endereço normal do YouTube, Vimeo, Loom ou de um arquivo .mp4.
    videoUrl: 'https://www.youtube.com/watch?v=aqz-KE-bpKQ',
    posterUrl: capa,
    aspect: '16x9',
    playLabel: 'Assistir',
    radius: 'xl',
    shadow: 'lg',
    ...props,
  });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'video.apresentacao',
    category: 'video',
    label: 'Vídeo de apresentação',
    description: 'Vídeo centralizado com título curto.',
    preview: 'video',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '960px', motion: 'fade' }, [
        b('ui.title', { text: 'Conheça em dois minutos', size: 'lg', level: 'h2', align: 'center' }),
        video(FOTO.escritorio),
      ]),
    ],
  },
  {
    key: 'video.com-texto',
    category: 'video',
    label: 'Vídeo ao lado do texto',
    description: 'Argumento à esquerda e vídeo à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Veja como funciona na prática', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Uma demonstração curta mostrando o dia a dia de quem usa a solução.' }),
        video(FOTO.painel),
      ]),
    ],
  },
  {
    key: 'video.escuro',
    category: 'video',
    label: 'Vídeo em fundo escuro',
    description: 'Fundo escuro com brilho realçando o player.',
    preview: 'video',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '1000px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade' },
        [b('ui.title', { text: 'Assista à demonstração', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }), video(FOTO.dados)],
      ),
    ],
  },
  {
    key: 'video.depoimento',
    category: 'video',
    label: 'Depoimento em vídeo',
    description: 'Vídeo de cliente com identificação abaixo.',
    preview: 'video',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '900px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Cliente desde 2021', variant: 'soft', align: 'center' }),
        video(FOTO.retrato),
        b('ui.paragraph', { text: 'Ana Martins — Diretora de operações, Transportes Souza', align: 'center', size: 'sm' }),
      ]),
    ],
  },
  {
    key: 'video.com-numeros',
    category: 'video',
    label: 'Vídeo com resultados',
    description: 'Números de prova ao lado do vídeo.',
    preview: 'video',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' }, [
        video(FOTO.reuniao),
        b('ui.stat', { value: '+38%', label: 'de propostas aceitas' }),
        b('ui.stat', { value: '7 min', label: 'de tempo médio de resposta' }),
      ]),
    ],
  },
  {
    key: 'video.com-cta',
    category: 'video',
    label: 'Vídeo com convite',
    description: 'Vídeo seguido de botão para falar com o time.',
    preview: 'video',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '960px', motion: 'fade-up' }, [
        video(FOTO.notebook),
        b('ui.buttons', { items: 'Quero uma demonstração|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },
  {
    key: 'video.largura-total',
    category: 'video',
    label: 'Vídeo em destaque total',
    description: 'Player largo, ocupando a faixa inteira da página.',
    preview: 'video',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'md', align: 'center', maxWidth: '1280px', motion: 'fade' }, [video(FOTO.arquitetura, { radius: 'lg' })]),
    ],
  },
  {
    key: 'video.tutorial',
    category: 'video',
    label: 'Vídeo tutorial',
    description: 'Vídeo com passo a passo resumido ao lado.',
    preview: 'split-left',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        video(FOTO.laptopCodigo),
        b('page.steps', {
          title: 'No vídeo você vai ver',
          layout: 'vertical',
          items: 'Cadastro|Como registrar clientes em segundos.\nProposta|Como enviar e acompanhar.\nFechamento|Como assinar sem papel.',
        }),
      ]),
    ],
  },
  {
    key: 'video.sobre-foto',
    category: 'video',
    label: 'Vídeo sobre foto de fundo',
    description: 'Fundo com foto escurecida e player centralizado.',
    preview: 'video',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { bleed: 'contained', backgroundImage: FOTO.industria, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '960px', motion: 'fade' },
        [b('ui.title', { text: 'Veja a operação por dentro', size: 'lg', level: 'h2', align: 'center' }), video(FOTO.industria)],
      ),
    ],
  },
  {
    key: 'video.completo',
    category: 'video',
    label: 'Vídeo completo',
    description: 'Selo, título, apoio, player e convite.',
    preview: 'video',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '1000px', pattern: 'dots', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Demonstração', variant: 'outline', align: 'center' }),
        b('ui.title', { text: 'Tudo o que você precisa ver em 3 minutos', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Sem enrolação: mostramos a tela real do sistema em uso.', align: 'center', maxWidth: '560px' }),
        video(FOTO.painel),
        b('ui.buttons', { items: 'Falar com um especialista|#formulario|solid', align: 'center' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
